import BaseComponent from '../../base/base_component';
import DOM from '../../utils/dom';
import Model from './scrollable_container_model';
import {CONTAINER_DIRECTION, SCROLLABLE_CONTAINER_CLASSES, PADDING_INDEX, SCROLL_DIRECTION} from './scrollable_container_consts';
import {USER_EVENTS} from '../../events/events';


let containerView = require('./view/container.html');
/**
 * ScrollableContainer allows to contain and manage items (HTML Elemens) with custom arrows.
 * Actually it overrides default overflow scrolling behaviour. Instead of default scrolling executes by clicking on arrows in PC and swipes in Mobile
 * Allows to define behavior in horizontal and vertical mode and smooth scrolling through the items
 */
class ScrollableContainerController extends BaseComponent {
    /**
     * @static
     * @type {string}
     */
    static CLASS = 'ScrollableContainerController';


    /**
     * Root component selector.
     *
     * @type {string}
     * @private
     */
    _selector = 'cgp-scrollable-container-component';


    _swiper = {
        startPosition: 0,
        movePosition: 0,
        endPosition: 0,
        maxDistance: 0,
        actualDistance: 0,
        initTransform: 0,
        transformBounders: {
            leftBounder: 0,
            rightBounder: 0
        },
        activeItem: 0,
        itemsBoundaries: []
    };


    init(params) {
        super.init(params);
        this._model = Model.create(params);
    }


    /**
     * Create overlay if one is not existed!
     *
     * @returns {*}
     * @public
     */
    start() {
        this.setHTML(this._getContainerView());
        this._setComponentID();
        this._appendItems();
        return this;
    }

    _subscribeToTouchEvents() {
        let container = this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.SCROLLING_CONTAINER);
        if (this._touchEvents && this._touchEvents.length > 0) {
            return;
        }
        this._touchEvents = [];

        this._touchEvents.push(DOM.onDOMElementEvent(container, 'touchstart', (e) => {
            this._onTouchStart(e);
        }));
        this._touchEvents.push(DOM.onDOMElementEvent(container, 'touchend', (e) => {
            this._onTouchEnd(e);
        }));
        this._touchEvents.push(DOM.onDOMElementEvent(container, 'touchmove', (e) => {
            this._onTouchMove(e);
        }));
    }


    /**
     * Handle event. From this point the component able to assign styles and calculate elements position
     */
    onAppendedToDOM() {
        super.onAppendedToDOM();
        // Avoid automatic style assignment. Set style over setStyle API
        this._subscribeToArrowsClick();
        this._assignStyle();
    }


    /**
     * Set container style
     */
    setStyle(params) {
        this._model.setStyle(params);
        this._clearStyles();
        this._assignStyle();
    }


    /**
     * Scroll to specified item index
     * @param itemIndex
     * @returns {*}
     */
    scrollTo(itemIndex) {
        if (1 > itemIndex) {
            this.logger.debug('itemIndex is less then 1. Scroll has been prevented');
            return Promise.resolve();
        }
        // supported only when itemsInRow = 1
        if (1 !== this._model.getGrid().itemsInRow) {
            this.logger.warn('Items in row are not equal to 1, scrolling to certain item is not supported');
            return Promise.reject();
        }
        // supported only if scrolling position is on init scrolling
        let scrollingStatus = this._getScrollingStatus();
        if (!scrollingStatus.isScrollExist) {
            this.logger.debug('There is no scrolling in container. Scroll to item ' + itemIndex + ' has been prevented');
            return Promise.resolve();
        }
        if (scrollingStatus.directions[SCROLL_DIRECTION.UP] || scrollingStatus.directions[SCROLL_DIRECTION.LEFT]) {
            this.logger.warn('Scrolling is not on initial position. Scrolling from other position is not supported yet');
            return Promise.reject();
        }
        let direction = this._model.getDirection();
        return this._scrollTo(direction === CONTAINER_DIRECTION.HORIZONTAL ? SCROLL_DIRECTION.RIGHT : SCROLL_DIRECTION.DOWN, itemIndex);
    }


    /**
     * Clear old styles
     * @private
     */
    _clearStyles() {
        DOM.removeClass(this.getEl(), SCROLLABLE_CONTAINER_CLASSES.HORIZONTAL_VIEW);
        DOM.removeClass(this.getEl(), SCROLLABLE_CONTAINER_CLASSES.VERTICAL_VIEW);
        DOM.removeClass(this._getItemsContainer(), SCROLLABLE_CONTAINER_CLASSES.SC_CENTER_ITEMS);
        DOM.removeClass(this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.SCROLLING_WRAPPER_CONTAINER), SCROLLABLE_CONTAINER_CLASSES.SC_ROW_GRID);
        DOM.removeStyle(this._getItemsContainer(), 'width');
        let itemElements = this._getItemsContainer().children;
        for (var i = 0; i < itemElements.length; i++) {
            DOM.removeStyle(itemElements[i], 'margin-right');
            DOM.removeStyle(itemElements[i], 'margin-bottom');
        }
    }


    /**
     * Set unique ID for component
     * @returns {ScrollableContainerController}
     * @private
     */
    _setComponentID() {
        this.getEl().id = this._model.getComponentID();
        return this;
    }

    /**
     * Get container view
     * @returns {*}
     * @private
     */
    _getContainerView() {
        return this.bake(containerView);
    }


    /**
     * Get component DOM element by its className
     * @param className
     * @returns {*}
     * @private
     */
    _getDOMElementByClassName(className) {
        return this.getEl().getElementsByClassName(className)[0];
    }


    /**
     * Get container for elements
     * @returns {*}
     * @private
     */
    _getItemsContainer() {
        return this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.SCROLLING_CONTAINER);
    }


    /**
     * Get component dimensions
     * @returns {{}}
     * @private
     */
    _getComponentDimensions() {
        return DOM.getDimensions(this.getEl());
    }


    /**
     * Create style selector with component ID
     * @param inlineStyle
     * @returns {string}
     * @private
     */
    _createStyleSelector(inlineStyle) {
        return '#' + this._model.getComponentID() + inlineStyle;
    }


    /**
     * Append items into container
     * @private
     */
    _appendItems() {
        let items = this._model.getItems();
        let container = this._getItemsContainer();
        items.forEach((item) => {
            DOM.addClass(item, SCROLLABLE_CONTAINER_CLASSES.ITEM_ELEMENT);
            DOM.append(container, item);
        });
        return this;
    }


    /**
     * Assign classes to container according to style options
     * @returns {ScrollableContainerController}
     * @private
     */
    _assignStyle() {
        let direction = this._model.getDirection();
        switch (direction) {
            case CONTAINER_DIRECTION.HORIZONTAL:
                this._prepareHorizontalView();
                break;
            case CONTAINER_DIRECTION.VERTICAL:
                this._prepareVerticalView();
                break;
            default:
                // maybe should throw an Exception here
                break;
        }
        this._setScrollerStyles();
        return this;
    }


    /**
     * Assing styles for scroller container
     * @private
     */
    _setScrollerStyles() {
        if (this._model.getIsSwiper() && this._model.getItems().length > 1) {
            this._setSwiperView();
        } else {
            if (this._model.getIsExternalScroll() === false) {
                DOM.addClass(this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.SCROLLING_WRAPPER_CONTAINER), SCROLLABLE_CONTAINER_CLASSES.SC_OVERFLOW_AUTO);
            }
            if (this._model.getIsCenterItems() && false === this._getScrollingStatus().isScrollExist) {
                DOM.addClass(this._getItemsContainer(), SCROLLABLE_CONTAINER_CLASSES.SC_CENTER_ITEMS);
            }
            if (true === this._model.getIsArrowVisible()) {
                this._assignArrowsPosition();
                this._changeArrowVisibility();
            }
        }

    }


    _setScrollableView() {

    }


    _getContainerWrapper() {
        return this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.SCROLLING_WRAPPER_CONTAINER);
    }


    _getSwiperInitTransform(offset) {
        let itemDimensions = this._getItemDimensions();
        let containerWrapper = this._getContainerWrapper();
        let wrapperWidth = DOM.getDimensions(containerWrapper).fullWidth;
        let initTransform = 0;
        switch (offset) {
            case 'center':
                initTransform = (wrapperWidth - (itemDimensions.fullWidth - itemDimensions.marginRight - itemDimensions.marginLeft)) / 2;
                break;
            case 'left':
            default:
                break;
        }
        return initTransform;
    }

    _setSwiperBoundaries() {
        let itemDimensions = this._getItemDimensions();
        let itemContainer = this._getItemsContainer();
        let multiplier = this.isRTL() ? -1 : 1;
        if (this.isRTL()) {
            this._swiper.transformBounders = {
                leftBounder: (this._swiper.initTransform + itemContainer.clientWidth - itemDimensions.fullWidth / 2),
                rightBounder: (this._swiper.initTransform + (itemDimensions.fullWidth / 2) * -1)
            }
        } else {
            this._swiper.transformBounders = {
                leftBounder: (this._swiper.initTransform + itemDimensions.fullWidth / 2),
                rightBounder: (this._swiper.initTransform + itemContainer.clientWidth - itemDimensions.fullWidth / 2) * (-1)
            }
        }

        this._swiper.itemsBoundaries = [];
        let items = this._model.getItems();
        let currentItemBoundaries =  {
            position: this._swiper.initTransform,
            left: this.isRTL() ? this._swiper.initTransform + itemDimensions.fullWidth / 2 : this._swiper.transformBounders.leftBounder,
            right: this.isRTL() ? this._swiper.transformBounders.rightBounder :this._swiper.initTransform - itemDimensions.fullWidth / 2,
        };

        for(let index = 0; index < items.length; index++) {
            let itemBoundaries = {
                position: currentItemBoundaries.position,
                left: currentItemBoundaries.left,
                right: currentItemBoundaries.right,
                index: index
            };
            if (index === items.length - 1) {
                // fix right limit
                itemBoundaries.right = this._swiper.transformBounders.rightBounder;
            }
            this._swiper.itemsBoundaries.push(itemBoundaries);
            currentItemBoundaries.position -= itemDimensions.fullWidth * multiplier;
            currentItemBoundaries.left -= itemDimensions.fullWidth * multiplier;
            currentItemBoundaries.right -= itemDimensions.fullWidth * multiplier;
        }

        this._swiper.maxDistance = itemDimensions.fullWidth * 1.3; // swipe distance larger than item in 50%
        this._swiper.transform = this._swiper.itemsBoundaries[this._swiper.activeItem].position;
    }

    /**
     * Add swiper for item container
     * @private
     */
    _setSwiperView() {
        let containerWrapper = this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.SCROLLING_WRAPPER_CONTAINER);
        DOM.addClass(this.getEl(), 'cgp-sc-swiper');
        let offset = this._model.getSwiperOffset();
        // TODO for horizontal only
        DOM.addStyle(containerWrapper, 'height', this._getItemDimensions().fullHeight + 'px');
        let multiplier = this.isRTL() ? -1 : 1;
        // init swiper transform
        this._swiper.initTransform = this._getSwiperInitTransform(offset) * multiplier;
        // set bounders
        this._setSwiperBoundaries();

        if (this._model.getIsHasDots() === true) {
            this._appendDots();
            this._activeDot(this._swiper.activeItem);
        }
        this._setTransform();
        this._subscribeToTouchEvents();
        if (true === this._model.getIsArrowVisible()) {
            this._assignArrowsPosition();
            this._changeArrowVisibility();
        }
    }


    /**
     * Disable swiper view
     * @private
     */
    _removeSwiperView() {
        DOM.removeClass(this.getEl(), 'cgp-sc-swiper');
        DOM.removeStyle(this._getItemsContainer(), 'transform');
        // detach touch events
        if (this._touchEvents) {
            this._touchEvents.forEach(subscription => subscription.remove());
        }
    }


    /**
     * Set transform style for container
     * @private
     */
    _setTransform() {
        DOM.addStyle(this._getItemsContainer(), 'transform', 'translateX(' + this._swiper.transform + 'px)');
    }


    /**
     * Create CSS rule for arrows padding
     * @returns {string}
     * @private
     */
    _createPaddingStyle() {
        // convert to rem
        let paddings = this._model.getArrowsPadding().map((padding) => {
            return padding / 10;
        });
        DOM.addStyle(this.getEl(), 'padding', paddings.join('rem ') + 'rem');
    }

    /**
     * Create CSS rule for component height style.
     * Component height should be maximum as height of its parent minus sum of top and bottom paddings
     * @returns {string}
     * @private
     */
    _createComponentHeightStyle() {
        return 'height:calc(100% - ' + (this._model.getArrowsPadding()[PADDING_INDEX.BOTTOM] + this._model.getArrowsPadding()[PADDING_INDEX.TOP]) + 'rem);';
    }


    /**
     * Create CSS rule for item margin, according to gap between items
     * @param rule
     * @param marginProperty
     * @returns {string}
     * @private
     */
    _createItemMarginRule(rule, marginProperty, gap, exceptFn) {
        let itemElements = this._getItemsContainer().children;
        let itemsInRow = this._model.getGrid().itemsInRow;
        let fullRows = parseInt(itemElements.length / itemsInRow);
        let partialRows = itemElements.length % itemsInRow;
        let lastRowElements = itemElements.length - (fullRows + partialRows - 1) * itemsInRow;
        // set default exception function. Except elements in last row
        exceptFn = exceptFn || ((index) => {
            // except last item in any case
            if (index === itemElements.length - 1) {
                return true;
            }
            return index > itemElements.length - lastRowElements - 1;
        });
        for (var i = 0; i < itemElements.length; i++) {
            let isExcepted = false;
            if (exceptFn) {
                isExcepted = exceptFn(i);
            }
            if (!isExcepted) {
                DOM.addStyle(itemElements[i], rule, (parseFloat(marginProperty) + gap) / 10 + 'rem');
            }
        }
    }


    /**
     * Add grid class in case of items in row larger than 1
     * @private
     */
    _assignGridSettings() {
        // TODO currently we support grid options only in vertical direction
        if (this._model.getDirection() === CONTAINER_DIRECTION.VERTICAL) {

            let grid = this._model.getGrid();
            if (1 < grid.itemsInRow) {
                DOM.addClass(this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.SCROLLING_WRAPPER_CONTAINER), SCROLLABLE_CONTAINER_CLASSES.SC_ROW_GRID);
                DOM.addStyle(this._getItemsContainer(), 'width', this._getItemDimensions().fullWidth * grid.itemsInRow + grid.gap * (grid.itemsInRow - 1) + 'rem');

                this._createItemMarginRule('margin-right', this._getItemDimensions().marginRight, grid.gap, (index) => {
                    return 0 === (index + 1) % grid.itemsInRow;
                });
            }
        }
    }


    /**
     * Get container item style
     * @returns {CSSStyleDeclaration|IEElementStyle}
     * @private
     */
    _getItemDimensions() {
        let itemElements = this._getItemsContainer().children;
        if (0 === itemElements.length) {
            return {marginRight: '0rem', marginBottom: '0rem'};
        }
        let item = itemElements[0];
        return Object.assign({}, DOM.getDimensions(item));
    }


    /**
     * Assign container to render items in horizontal
     * @private
     */
    _prepareHorizontalView() {
        DOM.addClass(this.getEl(), SCROLLABLE_CONTAINER_CLASSES.HORIZONTAL_VIEW);
        this._createPaddingStyle();
        //this._assignGridSettings();
        if (this.isRTL()) {
            this._createItemMarginRule('margin-left', this._getItemDimensions().marginLeft, this._model.getItemGap());
        } else {
            this._createItemMarginRule('margin-right', this._getItemDimensions().marginRight, this._model.getItemGap());
        }
    }


    /**
     * Assign container to render items in vertical
     * @private
     */
    _prepareVerticalView() {
        DOM.addClass(this.getEl(), SCROLLABLE_CONTAINER_CLASSES.VERTICAL_VIEW);
        this._createPaddingStyle();
        //this._assignGridSettings();
        this._createItemMarginRule('margin-bottom', this._getItemDimensions().marginBottom, this._model.getItemGap());
    }


    /**
     * Get dimension object of container item
     * @private
     */
    _getContainerItemDimensions() {
        let container = this._getItemsContainer();
        if (0 < container.children.length) {
            return DOM.getDimensions(container.children[0]);
        }
        return null;
    }


    /**
     * Calculate arrows absolute position style
     * @private
     */
    _assignArrowsPosition() {
        let padding = this._model.getArrowsPadding();
        let itemDimensions = this._getContainerItemDimensions();
        if (!itemDimensions) {
            this.logger.error('Failed in get dimension of container item. Unable to calculate arrows position');
            return;
        }
        Object.keys(SCROLL_DIRECTION).forEach((key) => {
            this._setArrowPositionByDirection(SCROLL_DIRECTION[key], padding, itemDimensions);
        });
    }


    isRTL () {
        return this.serviceFactory.languageService().getIsRTL();
    }


    /**
     * Set arrow position by scroll direction
     * @param arrowDirection
     * @private
     */
    _setArrowPositionByDirection(scrollDirection, arrowPaddings, itemDimensions) {
        let arrowEl, arrowDimensions;
        let wrapperDimensions = DOM.getDimensions(this._getContainerWrapper());
        switch (scrollDirection) {
            case SCROLL_DIRECTION.LEFT:
                arrowEl = this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.LEFT_ARROW);
                arrowDimensions = DOM.getDimensions(arrowEl);
                arrowEl.style.top = arrowPaddings[PADDING_INDEX.TOP] + ((wrapperDimensions.fullHeight - arrowDimensions.fullHeight) / 2) + 'px';
                arrowEl.style.left = ((arrowPaddings[PADDING_INDEX.LEFT] - arrowDimensions.fullWidth) / 2) + 'px';
                break;
            case SCROLL_DIRECTION.RIGHT:
                arrowEl = this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.RIGHT_ARROW);
                arrowDimensions = DOM.getDimensions(arrowEl);
                arrowEl.style.top = arrowPaddings[PADDING_INDEX.TOP] + ((wrapperDimensions.fullHeight - arrowDimensions.fullHeight) / 2) + 'px';
                arrowEl.style.right = ((arrowPaddings[PADDING_INDEX.RIGHT] - arrowDimensions.fullWidth) / 2) + 'px';
                break;
            case SCROLL_DIRECTION.UP:
                arrowEl = this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.UP_ARROW);
                arrowDimensions = DOM.getDimensions(arrowEl);
                arrowEl.style.top = arrowPaddings[PADDING_INDEX.TOP] + ((wrapperDimensions.fullHeight - arrowDimensions.fullHeight) / 2) / 10 + 'rem';
                arrowEl.style.left = ((this._getComponentDimensions().fullWidth - arrowDimensions.fullWidth) / 2) / 10 + 'rem';
                break;
            case SCROLL_DIRECTION.DOWN:
                arrowEl = this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.DOWN_ARROW);
                arrowDimensions = DOM.getDimensions(arrowEl);
                arrowEl.style.bottom = ((arrowPaddings[PADDING_INDEX.BOTTOM] - arrowDimensions.fullHeight) / 2) / 10 + 'rem';
                arrowEl.style.left = ((this._getComponentDimensions().fullWidth - arrowDimensions.fullWidth) / 2) / 10 + 'rem';
                break;
            default:
                /* Maybe should throw an exception */
                break;
        }
    }


    /**
     * Display or hidden arrows according to container overflow
     * If there is no space for all items or container has been scrolled, appropriate arrows will be displayed and vice versa
     * @private
     */
    _changeArrowVisibility() {
        // check if arrows should be invisible at all
        if (false === this._model.getIsArrowVisible()) {
            return;
        }
        let scrollingStatus = this._getScrollingStatus();
        Object.keys(scrollingStatus.directions).forEach((dir) => {
            switch (dir) {
                case SCROLL_DIRECTION.LEFT:
                    this._changeElementVisibility(this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.LEFT_ARROW), scrollingStatus.directions[dir]);
                    break;
                case SCROLL_DIRECTION.RIGHT:
                    this._changeElementVisibility(this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.RIGHT_ARROW), scrollingStatus.directions[dir]);
                    break;
                case SCROLL_DIRECTION.UP:
                    this._changeElementVisibility(this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.UP_ARROW), scrollingStatus.directions[dir]);
                    break;
                case SCROLL_DIRECTION.DOWN:
                    this._changeElementVisibility(this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.DOWN_ARROW), scrollingStatus.directions[dir]);
                    break;
                default:
                    /* Maybe should throw an exception */
                    break;
            }
        });
    }


    /**
     * Change element visibility
     * @param arrow
     * @param isVisible
     * @private
     */
    _changeElementVisibility(el, isVisible) {
        if (isVisible) {
            DOM.visible(el);
        } else {
            DOM.invisible(el);
        }
    }


    _getScrollDirectionValue(direction) {
        let value = direction;
        if (this.isRTL()) {
            value = direction === SCROLL_DIRECTION.LEFT ? SCROLL_DIRECTION.RIGHT : SCROLL_DIRECTION.LEFT;
        }

        return value;
    }

    /**
     * Get whether there is scrolling in container and scroll directions
     * @returns {{isScrollExist: boolean, directions: Array}}
     * @private
     */
    _getScrollingStatus() {
        let direction = this._model.getDirection();
        let scrollingStatus = {isScrollExist: false, directions: {
            [SCROLL_DIRECTION.LEFT]: false,
            [SCROLL_DIRECTION.RIGHT]: false,
            [SCROLL_DIRECTION.UP]: false,
            [SCROLL_DIRECTION.DOWN]: false
        }};
        if (this._model.getIsSwiper()) {
            let activeIndex = this._swiper.activeItem;
            let firstVisible = activeIndex > 0;
            let lastVisible =  activeIndex < this._model.getItems().length - 1;
            switch (direction) {
                case CONTAINER_DIRECTION.HORIZONTAL:
                    scrollingStatus.directions[this._getScrollDirectionValue(SCROLL_DIRECTION.LEFT)] = firstVisible;
                    scrollingStatus.directions[this._getScrollDirectionValue(SCROLL_DIRECTION.RIGHT)] = lastVisible;
                    break;
                case CONTAINER_DIRECTION.VERTICAL:
                    scrollingStatus.directions[SCROLL_DIRECTION.UP] = firstVisible;
                    scrollingStatus.directions[SCROLL_DIRECTION.DOWN] = lastVisible;
                    break;
                default:
                    /* Maybe should throw an exception */
                    break;
            }
        } else {
            let containerWrapper = this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.SCROLLING_WRAPPER_CONTAINER);
            switch (direction) {
                case CONTAINER_DIRECTION.HORIZONTAL:
                    if (0 === containerWrapper.scrollLeft) {
                        scrollingStatus.directions[SCROLL_DIRECTION.LEFT] = false;
                    } else {
                        scrollingStatus.directions[SCROLL_DIRECTION.LEFT] = true;
                    }
                    if (containerWrapper.scrollLeft >= containerWrapper.scrollWidth - containerWrapper.clientWidth || 0 === containerWrapper.clientWidth) {
                        scrollingStatus.directions[SCROLL_DIRECTION.RIGHT] = false;
                    } else {
                        scrollingStatus.directions[SCROLL_DIRECTION.RIGHT] = true;
                    }
                    break;
                case CONTAINER_DIRECTION.VERTICAL:
                    if (0 === containerWrapper.scrollTop) {
                        scrollingStatus.directions[SCROLL_DIRECTION.UP] = false;
                    } else {
                        scrollingStatus.directions[SCROLL_DIRECTION.UP] = true;
                    }
                    if (containerWrapper.scrollTop >= containerWrapper.scrollHeight - containerWrapper.clientHeight) {
                        scrollingStatus.directions[SCROLL_DIRECTION.DOWN] = false;
                    } else {
                        scrollingStatus.directions[SCROLL_DIRECTION.DOWN] = true;
                    }
                    break;
                default:
                    /* Maybe should throw an exception */
                    break;
            }
        }

        Object.keys(scrollingStatus.directions).forEach((key) => {
            if (false === scrollingStatus.isScrollExist && true === scrollingStatus.directions[key]) {
                scrollingStatus.isScrollExist = true;
            }
        });
        return scrollingStatus;
    }


    /**
     * Subscribe to arrows click
     * @private
     */
    _subscribeToArrowsClick() {
        this._arrowDOMEvents = [];
        this._arrowDOMEvents.push(DOM.onDOMElementEvent(this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.LEFT_ARROW), USER_EVENTS.ON_CLICK, () => {
            this._scrollTo(SCROLL_DIRECTION.LEFT);
        }));
        this._arrowDOMEvents.push(DOM.onDOMElementEvent(this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.RIGHT_ARROW), USER_EVENTS.ON_CLICK, () => {
            this._scrollTo(SCROLL_DIRECTION.RIGHT);
        }));
        this._arrowDOMEvents.push(DOM.onDOMElementEvent(this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.UP_ARROW), USER_EVENTS.ON_CLICK, () => {
            this._scrollTo(SCROLL_DIRECTION.UP);
        }));
        this._arrowDOMEvents.push(DOM.onDOMElementEvent(this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.DOWN_ARROW), USER_EVENTS.ON_CLICK, () => {
            this._scrollTo(SCROLL_DIRECTION.DOWN);
        }));
    }


    /**
     * Calculate scrolling size, according to component direction
     * @returns {number|*}
     * @private
     */
    _getScrollingSize(steps) {
        let itemDimensions = this._getItemDimensions();
        let containerDirection = this._model.getDirection();
        let scrollSize = containerDirection === CONTAINER_DIRECTION.HORIZONTAL ? itemDimensions.fullWidth : itemDimensions.fullHeight;
        return scrollSize * (steps || this._model.getScrollingStep());
    }


    /**
     * Scroll items into the container
     * @param direction
     * @param container
     * @param item
     * @param steps
     * @private
     */
    _scrollTo(direction, steps) {
        if (this._model.getIsSwiper() === true) {
            let item = this._getSwipeItemByDirection(direction);
            this._swipeToActive(item);
        } else {
            let scrollingSize = this._getScrollingSize(steps);
            this.logger.debug('ScrollingSize = ' + scrollingSize);
            return this._smoothScrolling(direction, scrollingSize, this._getDOMElementByClassName(SCROLLABLE_CONTAINER_CLASSES.SCROLLING_WRAPPER_CONTAINER));
        }
     }


    _getSwipeItemByDirection(direction) {
        direction = this._getScrollDirectionValue(direction);
        let itemIndex = -1;
        switch (direction) {
            case SCROLL_DIRECTION.LEFT:
                itemIndex = this._swiper.activeItem - 1;
                break;
            case SCROLL_DIRECTION.RIGHT:
                itemIndex = this._swiper.activeItem + 1;
                break;
            case SCROLL_DIRECTION.UP:
                itemIndex = this._swiper.activeItem - 1;
                break;
            case SCROLL_DIRECTION.DOWN:
                itemIndex = this._swiper.activeItem + 1;
                break;
            default:
                /* Maybe should throw an exception */
                break;
        }

        return this._getItemBoundaries(itemIndex);
    }

    /**
     * Scroll with smooth animation
     * @param direction
     * @param scrollSize
     * @param container
     * @param duration
     * @param smooothLevel
     * @private
     */
    _smoothScrolling(direction, scrollSize, container) {
        return new Promise((res) => {
            let smoothLevel = 20;
            let stepInterval = Math.ceil(this._model.getScrollingDuration() / smoothLevel); // 1000 / 20 = 50
            let restSize = scrollSize;
            let scrollStepSize = Math.ceil(scrollSize / smoothLevel); // 100 / 50 = 2 (0.5 rest)

            let scrollStepFn = () => {
                scrollSize = restSize > scrollStepSize ? scrollStepSize : restSize;
                switch (direction) {
                    case SCROLL_DIRECTION.LEFT:
                        container.scrollLeft -= scrollSize;
                        break;
                    case SCROLL_DIRECTION.RIGHT:
                        container.scrollLeft += scrollSize;
                        break;
                    case SCROLL_DIRECTION.UP:
                        container.scrollTop -= scrollSize;
                        break;
                    case SCROLL_DIRECTION.DOWN:
                        container.scrollTop += scrollSize;
                        break;
                    default:
                        /* Maybe should throw an exception */
                        break;
                }
                restSize -= scrollSize;
                if (0 < restSize) {
                    DOM.setTimeout(scrollStepFn, stepInterval);
                } else {
                    this.logger.debug('Finished scrolling');
                    this._changeArrowVisibility();
                    res();
                }
            };
            scrollStepFn();
            // DOM.setTimeout(scrollStepFn, stepInterval);
        });

    }


    /**
     * Disable scrolling and add appropriate style
     */
    lock() {
        if (this._arrowDOMEvents) {
            this._arrowDOMEvents.forEach(subscription => subscription.remove());
        }

        DOM.addClass(this.getEl(), 'cgp-sc-lock');
    }


    onSizeChanged() {
        this._changeArrowVisibility();
    }


    /**
     * Remove item from container by index
     * @param itemIndex
     */
    removeItem(itemIndex, animationOn = true) {
        let items = this._model.getItems();
        if (!items || items.length - 1 < itemIndex) {
            return Promise.reject();
        }
        return new Promise((res) => {
            let removedItem = items[itemIndex];
            this._removeItemHandler(itemIndex, removedItem, res, animationOn);
        });
    }


    _removeItemHandler(index, itemEl, resolver, animationOn = true) {
        if (animationOn) {
            let cleanFn = DOM.onDOMEvent(itemEl, 'animationend', () => {
                DOM.setTimeout(() => {
                    this._removeItem(index, itemEl);
                    // TO DO - _domEventsSubscriptions is a private member. It should not be accessed like this
                    this._domEventsSubscriptions.forEach(domEvent => {
                        domEvent.remove();
                        this.logger.debug('Detach animationend event from item');
                        this._onItemRemoved(index, resolver);
                    });
                }, 0);
            });
            // TO DO - _domEventsSubscriptions is a private member. It should not be accessed like this
            this._domEventsSubscriptions = [cleanFn];
            DOM.toggleClass(itemEl, 'cgp-sc-removed-item');
        } else {
            this._removeItem(index, itemEl);
            this._onItemRemoved(index, resolver);
        }
    }


    _onItemRemoved(index, resolver) {
        if (this._model.getIsSwiper()) {
            this._swiper.activeItem = index - 1 > -1 ? index - 1 : 0;
            if (this._model.getItems().length === 1) {
                // remove swiper styles
                this._removeSwiperView();
            }
            this._setScrollerStyles();
            resolver(this._swiper.activeItem);
        } else {
            this._setScrollerStyles();
            resolver();
        }
    }

    _removeItem(index, itemEl) {
        DOM.removeElement(this._getItemsContainer(), itemEl);
        this._model.getItems().splice(index, 1);
    }


    /**
     * Handle on touch event
     * Set swiper start and last position
     * @param e
     * @private
     */
    _onTouchStart(e) {
        let clientX = e.touches && e.touches.length > 0 ? e.touches[0].clientX : e.clientX;
       // console.log('TOUCH', 'touchstart XPosition = ' + clientX);
        this._swiper.startPosition = clientX;
        this._swiper.lastPosition = this._swiper.startPosition;
        this._swiper.stopScrolling = false;
    }

    /**
     * Handle on touch end event
     * @param e
     * @private
     */
    _onTouchEnd() {
        this._swiper.startPosition = 0;
        let activeItem = this._getActiveItemIndexByTransform();
        this._swipeToActive(activeItem);
    }


    _swipeToActive(activeItem) {
        if (activeItem) {
            this._swipeTo(activeItem.position)
                .then(() => {
                    this._activeDot(activeItem.index);
                    //console.log('TOUCH', 'Active item index = ' + activeItem.index);
                    if (activeItem.index !== this._swiper.activeItem) {
                        this._swiper.activeItem = activeItem.index;
                        this.onActiveChanged && this.onActiveChanged(this._swiper.activeItem);
                        this._changeArrowVisibility();
                    }
                });
        }
    }

    _getItemBoundaries(itemIndex) {
        let item = this._swiper.itemsBoundaries.find((itemBoundary) => {
            return itemIndex === itemBoundary.index;
        });
        return item;
    }


    /**
     * @param (index: number) => void
     */
    onActiveChanged = undefined;


    /**
     * Swipe container to specified position
     * @param position
     * @private
     */
    _swipeTo(position) {
        return new Promise((res) => {
            let multiplier = 1;
            if (position < this._swiper.transform) {
                multiplier = -1; // right

            }

            let velocity = this._getItemDimensions().fullWidth / 500; //315rem per second
            let intervalTime = 20;
            let stepSize = velocity * intervalTime;
            let requestId;
            let stepFn = () => {
                if (this._swiper.transform === position) {
                    if (requestId) {
                        window.cancelAnimationFrame(requestId);
                    }
                    return res();
                }
                let step = stepSize * multiplier;
                let nextPosition = this._swiper.transform + step;
                if (multiplier < 0 && nextPosition < position ) { // right stop condition
                    this._swiper.transform = position;
                } else if (multiplier > 0 && nextPosition > position) { // left stop condition
                    this._swiper.transform = position;
                } else {
                    this._swiper.transform += step;
                }
                this._setTransform();
                //timeoutRef = DOM.setTimeout(stepFn, intervalTime);
                requestId= window.requestAnimationFrame(stepFn);
            };
            stepFn();
        });
    }


    _getActiveItemIndexByTransform() {
        let transform = this._swiper.transform;
        let item = this._swiper.itemsBoundaries.find((itemBoundary) => {
            let predicate = itemBoundary.left >= transform && itemBoundary.right <= transform;
            return predicate;
        });
        return item;
    }


    _onTouchMove(e) {
        if (this._swiper.stopScrolling === true) {
            return;
        }

        if (this._swiper.startPosition === 0) {
            return; // not started
        }

        let movePosition = e.touches && e.touches.length > 0 ? e.touches[0].clientX : e.clientX;
        //console.log('TOUCH', ' touchmove XPosition = ' + movePosition);
        let multiplier = 1;
        let moveDistance = Math.abs(this._swiper.lastPosition - movePosition);
        //console.log('TOUCH', ' touchmove moveDistance = ' + moveDistance);
        if (this._swiper.lastPosition > movePosition) {
            //transform right
            multiplier = -1;
        }
        this._swiper.lastPosition = movePosition;

        let prevActualDistance = this._swiper.actualDistance;
        this._swiper.actualDistance =  Math.abs(this._swiper.startPosition - movePosition);
        //console.log('TOUCH', ' touchmove actualDistance = ' + this._swiper.actualDistance);
        // stop transform
        if (this._swiper.maxDistance < this._swiper.actualDistance) {
            this._swiper.actualDistance = this._swiper.maxDistance;
            //console.log('TOUCH', ' touchmove maxDistanceLimit');
            moveDistance = this._swiper.maxDistance - prevActualDistance; // step
           // console.log('TOUCH', ' touchmove change move distance to = ' + moveDistance);
            this._swiper.transform += moveDistance * multiplier;
            this._setTransform();
            this._swiper.stopScrolling = true;
            return;
        }
        let transform = this._swiper.transform + moveDistance * multiplier;
        if (this._swiper.transformBounders.leftBounder < transform) {
            //console.log('TOUCH', ' touchmove Left boundaries limit, transform = ' + transform);
            this._swiper.transform = this._swiper.transformBounders.leftBounder;
            this._setTransform();
            this._swiper.stopScrolling = true;
            return;
           // this._swiper.actualDistance = Math.abs(this._swiper.startPosition - this._swiper.actualDistance
        }
        if (this._swiper.transformBounders.rightBounder > transform) {
            //console.log('TOUCH', ' touchmove Right boundaries limit, transform = ' + transform);
            this._swiper.transform = this._swiper.transformBounders.rightBounder;
            this._setTransform();
            this._swiper.stopScrolling = true;
            return;
        }
        // calculate transform
        this._swiper.transform += moveDistance * multiplier;
        this._setTransform();
    }


    /**
     * Add dots for swipe items
     * @returns {ScrollableContainerController}
     * @private
     */
    _appendDots() {
        let items = this._model.getItems();
        let dotContainer = this._getDOMElementByClassName('cgp-cs-items-dots');
        // clear previous child elements
        while (dotContainer.firstChild) {
            dotContainer.removeChild(dotContainer.firstChild);
        }
        for(let index = 0; index < items.length; index++) {
            let dotEl = DOM.createElement();
            DOM.addClass(dotEl, 'cgp-item-dot');
            DOM.addClass(dotEl, 'cgp-themes-item-dot');
            DOM.append(dotContainer, dotEl);
        }
        return this;
    }


    _activeDot(index) {
        let dotContainer = this._getDOMElementByClassName('cgp-cs-items-dots');
        let dots = dotContainer.children;
        for (let i = 0; i < dots.length; i++) {
            if (i === index) {
                DOM.addClass(dots[i], 'cgp-sc-dot-active');
            } else {
                DOM.removeClass(dots[i], 'cgp-sc-dot-active');
            }
        }
    }


    /**
     * Clear allocated styles
     */
    shutDown() {
        super.shutDown();
        this.logger.debug('Shut down');
    }
}

export default ScrollableContainerController;